import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { BannerRotator } from 'cityads-v3/types/bannerRotator'
import { Offer } from 'cityads-v3/types/offers'
import { RootState } from 'cityads-v3/types/reducer'
import { useCreatePage } from './Create.hooks'
import { usePreloadOffers } from 'cityads-v3/hooks/offers'
import classes from './Create.scss'

import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Divider from '@mui/material/Divider'
import FormControlLabel from '@mui/material/FormControlLabel'
import GetCode from './GetCode'
import SelectField from 'cityads-v3/components/TextField/SelectField'
import Subaccounts from 'cityads-v3/components/Forms/Subaccounts'
import TextField from 'cityads-v3/components/TextField/TextField'
import Typography from '@mui/material/Typography'
import WaitingButton from 'cityads-v3/components/Buttons/WaitingButton'

export type BannerRotatorCreateProps = {
  offerId?: Offer['id'],
  onSuccess?: (bannerRotator: BannerRotator) => void
}

export default function Create({
  offerId,
  onSuccess,
}: BannerRotatorCreateProps): JSX.Element {
  const { t } = useTranslation(['BannerRotator', 'main']);
  const pageParams = useParams<{ offerId?: string }>()
  const { form, formState, entityList, handleResetForm } = useCreatePage(+pageParams.offerId || offerId, onSuccess)
  const offerSelectorEnabled = useMemo(() => !offerId, [offerId]);
  const bannerRotator = useSelector((state: RootState) => state.bannerRotator.lastCreatedRotator)

  const offerList = useMemo(() => entityList.offerList[0]?.offer.map(item => ({ name: item.translated_name || item.name, value: item.id })) ?? [], [entityList.offerList[0]?.offer])
  const offerListFull = useMemo(() => entityList.offerListFull?.map(item => ({ name: item.translated_name || item.name, value: item.id })) ?? [], [entityList.offerListFull])
  const categoryList = useMemo(() => entityList.categoryList[0]?.category.map(item => ({ name: item.name, value: item.id })) ?? [], [entityList.categoryList[0]?.category])
  const promoList = useMemo(() => entityList.promoList[0]?.promo.map(item => ({ name: item.name, value: item.id })) ?? [], [entityList.promoList[0]?.promo])
  const promoListFull = useMemo(() => entityList.promoListFull?.map(item => ({ name: item.name, value: item.id })) ?? [], [entityList.promoListFull])

  usePreloadOffers(pageParams.offerId ? [+pageParams.offerId] : []);

  return (
    <>
      <div className={classes.form}>
        <TextField
          fullWidth
          label={t('create.name')}
          disabled={formState.isSent}
          error={form.fields.name.error}
          helperText={form.fields.name.errorText}
          maxLength={255}
          renderMinMaxLength={false}
          onChange={form.fields.name.handleChange}
          value={form.fields.name.value}
          variant="outlined"
        />

        <SelectField<number[], number>
          fullWidth
          multiple
          label={t('create.bannerSize')}
          disabled={formState.isSent}
          items={entityList.bannerSizeList[0]?.bannerSize.map(item => ({ name: item.name, value: item.id })) ?? []}
          error={form.fields.banner_sizes.error}
          helperText={form.fields.banner_sizes.errorText}
          handleChange={form.fields.banner_sizes.setValue}
          value={form.fields.banner_sizes.value}
          variant="outlined"
          searchProps={{
            isLoading: false,
            frontFilter: true,
            onChange: entityList.filterForm.fields.bannerSize.handleChange,
            value: entityList.filterForm.fields.bannerSize.value
          }}
        />

        <SelectField<number[], number>
          alwaysShowSelected
          fullWidth
          multiple
          label={t('create.offers')}
          disabled={formState.isSent}
          disabledValues={offerSelectorEnabled ? [...form.fields.exclude_offer_ids.value] : [offerId, ...form.fields.exclude_offer_ids.value]}
          items={offerList}
          additionalDictionary={offerListFull}
          error={form.fields.offer_ids.error}
          helperText={form.fields.offer_ids.errorText}
          handleChange={form.fields.offer_ids.setValue}
          value={form.fields.offer_ids.value}
          searchProps={{
            isLoading: entityList.offerList[1],
            onChange: entityList.filterForm.fields.offerName.handleChange,
            value: entityList.filterForm.fields.offerName.value
          }}
          variant="outlined"
        />

        <SelectField<number[], number>
          alwaysShowSelected
          fullWidth
          multiple
          label={t('create.offersExclude')}
          disabled={formState.isSent}
          disabledValues={form.fields.offer_ids.value}
          items={offerList}
          additionalDictionary={offerListFull}
          error={form.fields.exclude_offer_ids.error}
          helperText={form.fields.exclude_offer_ids.errorText}
          handleChange={form.fields.exclude_offer_ids.setValue}
          value={form.fields.exclude_offer_ids.value}
          searchProps={{
            isLoading: entityList.offerList[1],
            onChange: entityList.filterForm.fields.offerName.handleChange,
            value: entityList.filterForm.fields.offerName.value
          }}
          variant="outlined"
        />

        <SelectField<number[], number>
          fullWidth
          multiple
          label={t('create.categories')}
          disabled={formState.isSent}
          disabledValues={form.fields.exclude_category_ids.value}
          items={categoryList}
          error={form.fields.category_ids.error}
          helperText={form.fields.category_ids.errorText}
          handleChange={form.fields.category_ids.setValue}
          value={form.fields.category_ids.value}
          variant="outlined"
          searchProps={{
            isLoading: false,
            frontFilter: true,
            onChange: entityList.filterForm.fields.categoryName.handleChange,
            value: entityList.filterForm.fields.categoryName.value
          }}
        />

        <SelectField<number[], number>
          fullWidth
          multiple
          label={t('create.categoriesExclude')}
          disabled={formState.isSent}
          disabledValues={form.fields.category_ids.value}
          items={categoryList}
          error={form.fields.exclude_category_ids.error}
          helperText={form.fields.exclude_category_ids.errorText}
          handleChange={form.fields.exclude_category_ids.setValue}
          value={form.fields.exclude_category_ids.value}
          variant="outlined"
          searchProps={{
            isLoading: false,
            frontFilter: true,
            onChange: entityList.filterForm.fields.categoryName.handleChange,
            value: entityList.filterForm.fields.categoryName.value
          }}
        />

        <SelectField<number[], number>
          alwaysShowSelected
          fullWidth
          multiple
          label={t('create.banners')}
          disabled={formState.isSent}
          disabledValues={form.fields.exclude_banner_ids.value}
          items={promoList}
          additionalDictionary={promoListFull}
          error={form.fields.banner_ids.error}
          helperText={form.fields.banner_ids.errorText}
          handleChange={form.fields.banner_ids.setValue}
          value={form.fields.banner_ids.value}
          searchProps={{
            isLoading: entityList.promoList[1],
            onChange: entityList.filterForm.fields.bannerName.handleChange,
            value: entityList.filterForm.fields.bannerName.value
          }}
          variant="outlined"
        />

        <SelectField<number[], number>
          alwaysShowSelected
          fullWidth
          multiple
          label={t('create.bannersExclude')}
          disabled={formState.isSent}
          disabledValues={form.fields.banner_ids.value}
          items={promoList}
          additionalDictionary={promoListFull}
          error={form.fields.exclude_banner_ids.error}
          helperText={form.fields.exclude_banner_ids.errorText}
          handleChange={form.fields.exclude_banner_ids.setValue}
          value={form.fields.exclude_banner_ids.value}
          searchProps={{
            isLoading: entityList.promoList[1],
            onChange: entityList.filterForm.fields.bannerName.handleChange,
            value: entityList.filterForm.fields.bannerName.value
          }}
          variant="outlined"
        />

      </div>
      <div className={classes.form}>
        <Subaccounts
          disabled={formState.isSent}
          error={form.fields.subaccounts.error}
          helperText={form.fields.subaccounts.errorText}
          onChange={form.fields.subaccounts.setValue}
          value={form.fields.subaccounts.value}
          variant="outlined"
        />

        <div className={classes.checkboxes}>
          <FormControlLabel
            control={
              <Checkbox
                disabled={formState.isSent}
                checked={form.fields.add_new_offer.value}
                onChange={form.fields.add_new_offer.toggle} />
            }
            label={t('create.addNewOffers')}
          />

          <FormControlLabel
            control={
              <Checkbox
                disabled={formState.isSent}
                checked={form.fields.enable_optimization.value}
                onChange={form.fields.enable_optimization.toggle} />
            }
            label={t('create.enableOptimization')}
          />
        </div>
      </div>

      {formState.isSent ? (
        <>
          <Divider />
          <Typography variant="h6">{t('code')}</Typography>
          <GetCode
            bannerRotator={bannerRotator}
            buttons={
              <Button onClick={handleResetForm}>{t('createOneMoreRotator')}</Button>
            }
          />
        </>
      ) : (
        <>
          <WaitingButton
            color="primary"
            variant="contained"
            waiting={formState.isSending}
            onClick={form.handleSubmit}
          >{t('main:buttons.save')}</WaitingButton>
        </>
      )}
    </>
  )
}
